import React, { useState, useMemo } from 'react';
import { ChevronLeft, ChevronRight, Trash2, Search, X } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { format } from 'date-fns';

const API = '/api';

const LeadsTab = ({ 
  leads, 
  leadsPage, 
  setLeadsPage, 
  leadsTotalPages, 
  fetchData 
}) => {
  const [searchQuery, setSearchQuery] = useState('');

  // Filter leads based on search query
  const filteredLeads = useMemo(() => {
    if (!searchQuery.trim()) return leads;
    const query = searchQuery.toLowerCase();
    return leads.filter(lead => 
      lead.name?.toLowerCase().includes(query) ||
      lead.email?.toLowerCase().includes(query) ||
      lead.phone?.toLowerCase().includes(query) ||
      lead.status?.toLowerCase().includes(query) ||
      lead.source?.toLowerCase().includes(query)
    );
  }, [leads, searchQuery]);

  return (
    <div>
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-4">
        <h3 className="text-xl font-bold">Leads Management</h3>
        
        {/* Search Input */}
        <div className="flex items-center gap-4 w-full sm:w-auto">
          <div className="relative flex-1 sm:flex-none">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search by name, email, phone..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full sm:w-64 pl-10 pr-8 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FF4500] focus:border-transparent"
              data-testid="leads-search-input"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
          
          {/* Pagination */}
          <div className="flex items-center gap-2">
            <button
              onClick={() => setLeadsPage(p => Math.max(1, p - 1))}
              disabled={leadsPage === 1}
              className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <span className="text-sm text-[#525252]">
              Page {leadsPage} of {leadsTotalPages}
            </span>
            <button
              onClick={() => setLeadsPage(p => Math.min(leadsTotalPages, p + 1))}
              disabled={leadsPage === leadsTotalPages}
              className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Search Results Count */}
      {searchQuery && (
        <p className="text-sm text-[#525252] mb-3">
          Found {filteredLeads.length} result{filteredLeads.length !== 1 ? 's' : ''} for "{searchQuery}"
        </p>
      )}

      <div className="overflow-x-auto">
        <table className="w-full" data-testid="leads-table">
          <thead>
            <tr className="border-b border-gray-200">
              <th className="text-left py-3 px-4 text-sm font-semibold text-[#0A0A0A]">Name</th>
              <th className="text-left py-3 px-4 text-sm font-semibold text-[#0A0A0A]">Email</th>
              <th className="text-left py-3 px-4 text-sm font-semibold text-[#0A0A0A]">Phone</th>
              <th className="text-left py-3 px-4 text-sm font-semibold text-[#0A0A0A]">Source</th>
              <th className="text-left py-3 px-4 text-sm font-semibold text-[#0A0A0A]">Status</th>
              <th className="text-left py-3 px-4 text-sm font-semibold text-[#0A0A0A]">Visits</th>
              <th className="text-left py-3 px-4 text-sm font-semibold text-[#0A0A0A]">Notes</th>
              <th className="text-left py-3 px-4 text-sm font-semibold text-[#0A0A0A]">Last Seen</th>
              <th className="text-left py-3 px-4 text-sm font-semibold text-[#0A0A0A]">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredLeads.map((lead) => (
              <tr key={lead.id} className="border-b border-gray-100 hover:bg-gray-50">
                <td className="py-3 px-4 text-sm">
                  <div className="flex items-center gap-2">
                    {lead.name}
                    {lead.is_returning && (
                      <span className="px-2 py-0.5 bg-purple-100 text-purple-700 text-xs font-semibold rounded-full">
                        Returning
                      </span>
                    )}
                  </div>
                </td>
                <td className="py-3 px-4 text-sm">{lead.email}</td>
                <td className="py-3 px-4 text-sm">{lead.phone}</td>
                <td className="py-3 px-4">
                  <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold ${
                    lead.source === 'facebook' ? 'bg-[#1877F2] text-white' :
                    lead.source === 'instagram' ? 'bg-[#E4405F] text-white' :
                    lead.source === 'google' ? 'bg-[#4285F4] text-white' :
                    lead.source === 'tiktok' ? 'bg-[#000000] text-white' :
                    lead.source === 'linkedin' ? 'bg-[#0A66C2] text-white' :
                    lead.source === 'whatsapp' ? 'bg-[#25D366] text-white' :
                    'bg-gray-200 text-[#0A0A0A]'
                  }`}>
                    {lead.source || 'direct'}
                  </span>
                </td>
                <td className="py-3 px-4">
                  <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold ${
                    lead.status === 'paid' ? 'bg-[#25D366] text-white' :
                    lead.status === 'completed' ? 'bg-[#0055FF] text-white' :
                    lead.status === 'booked' ? 'bg-[#FFD60A] text-[#0A0A0A]' :
                    'bg-gray-200 text-[#0A0A0A]'
                  }`}>
                    {lead.status}
                  </span>
                </td>
                <td className="py-3 px-4">
                  <span className={`inline-block px-2 py-1 rounded text-xs font-semibold ${
                    (lead.visit_count || 1) > 1 ? 'bg-purple-100 text-purple-700' : 'bg-gray-100 text-gray-600'
                  }`}>
                    {lead.visit_count || 1}x
                  </span>
                </td>
                <td className="py-3 px-4">
                  <input
                    type="text"
                    defaultValue={lead.notes || ''}
                    maxLength={50}
                    placeholder="Add note..."
                    className="w-24 px-2 py-1 text-xs bg-gray-50 border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-[#FF4500]"
                    onBlur={async (e) => {
                      const newNote = e.target.value;
                      if (newNote !== (lead.notes || '')) {
                        const token = localStorage.getItem('admin_token');
                        try {
                          await axios.patch(`${API}/leads/${lead.id}`, { notes: newNote }, {
                            headers: { Authorization: `Bearer ${token}` }
                          });
                          toast.success('Note saved');
                        } catch (error) {
                          toast.error('Failed to save note');
                        }
                      }
                    }}
                  />
                </td>
                <td className="py-3 px-4 text-sm text-gray-600">
                  {lead.last_seen ? format(new Date(lead.last_seen), 'MMM d, h:mm a') : format(new Date(lead.created_at), 'MMM d, h:mm a')}
                </td>
                <td className="py-3 px-4">
                  <button
                    onClick={async () => {
                      if (!window.confirm(`Delete lead "${lead.name}"?`)) return;
                      const token = localStorage.getItem('admin_token');
                      try {
                        await axios.delete(`${API}/leads/${lead.id}`, {
                          headers: { Authorization: `Bearer ${token}` }
                        });
                        toast.success('Lead deleted');
                        fetchData();
                      } catch (error) {
                        toast.error('Failed to delete lead');
                      }
                    }}
                    className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                    title="Delete lead"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {filteredLeads.length === 0 && (
          <p className="text-center text-[#525252] py-8">
            {searchQuery ? 'No leads match your search' : 'No leads yet'}
          </p>
        )}
      </div>
    </div>
  );
};

export default LeadsTab;
